import {
	Box,
	Grid,
	IconButton,
	InputAdornment,
	Stack,
	TextField,
	Typography,
	styled,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { debounce } from 'lodash';

import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import { PAGINATION_CONSTANTS } from 'common/constants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setRegInvententoryAddToGroupDrawer,
	setRegInvententoryCreateDrawer,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { fetchGroupsSummaryAPIData } from 'features/RegulationInventory/api';

import {
	ICitationGroup,
	IGroupsDataParams,
	IGroupsTableList,
	IGroupsTableResponse,
} from '../../api/type-def';

import styles from './RegulationInventoryAddToGroup.module.scss';

const { backgroundColor, borderColor } = styles;

const { PAGE_SIZE, PAGE_NUMBER } = PAGINATION_CONSTANTS;

const StyledTextField = styled(TextField)(() => ({
	background: backgroundColor,
	borderRadius: '4px',
	border: `1px solid ${borderColor}`,
	width: '60%',
}));

interface IRegulatoryInventoryAddToGroup {
	onAddHandler: (groupIds: ICitationGroup[]) => void;
	objectId: string;
}

const RegulatoryInventoryAddToGroup = (
	props: IRegulatoryInventoryAddToGroup,
) => {
	const { onAddHandler, objectId } = props;
	const { tableQueryObj, updateTableQuery } = useTableQuery({
		id: 'tableData',
	});
	const [selectedGroup, setSelectedGroup] = useState<ICitationGroup[]>([]);
	const [searchText, setSearchText] = useState<string>('');
	const [isLoading, setIsLoading] = useState(false);
	const [groupsData, setGroupsData] = useState<IGroupsTableList[]>([]);
	const [groupsOriginalData, setGroupsOriginalData] =
		useState<IGroupsTableResponse>();

	const { drawerCloseIconColor } = styles;

	const onCancelHandler = () => {
		drawerCloseHandler();
	};
	const defaultPage = {
		sortKey: '',
		pageSize: PAGE_SIZE,
		pageNumber: PAGE_NUMBER,
		sortDirection: '',
	};
	const addNewGroup = useCallback(() => {
		dispatch(setRegInvententoryCreateDrawer(true));
	}, [setRegInvententoryCreateDrawer]);

	const dispatch = useDispatch();

	const newlyAddedGroup: any = useSelector(
		(state: RootState) => state.common.regInvententoryCreateDrawer,
	);
	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	useEffect(() => {
		if (newlyAddedGroup === false) {
			getGroupsTableData('', defaultPage);
		}
	}, [newlyAddedGroup]);

	useEffect(() => {
		getGroupsTableData('', defaultPage);
	}, []);

	const addGroupDrawerState: any = useSelector(
		(state: RootState) => state.common.regInvententoryAddToGroupDrawer,
	);

	const drawerCloseHandler = useCallback(() => {
		dispatch(setRegInvententoryAddToGroupDrawer(!addGroupDrawerState));
	}, [addGroupDrawerState]);

	const onRowCheckboxSelect = (selectedId: any) => {
		const groups: ICitationGroup[] = [];
		if (selectedId.length) {
			selectedId.forEach((ele: string) => {
				const addGroup = groupsData.find((e) => e.groupId === ele);
				if (addGroup) {
					groups.push({
						groupId: addGroup.groupId,
						groupName: addGroup.groupName,
					} as ICitationGroup);
				}
			});
		}
		setSelectedGroup(groups);
	};

	const onAddGroupsHandler = () => {
		onAddHandler(selectedGroup);
	};

	const getGroupsTableData = async (searchText: string, data: any) => {
		setIsLoading(true);
		const payload: IGroupsDataParams = {
			createdBy: [loggedInUser?.userId || ''],
			objectId,
			priority: [],
			taskStatus: '',
		};
		const response = await fetchGroupsSummaryAPIData(searchText, data, payload);
		if (response?.responseBody) {
			const responseData = response?.responseBody;
			setGroupsOriginalData(responseData);
			setGroupsData(responseData.citationGroupAndTaskDetails);
			setIsLoading(false);
		} else {
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
			setGroupsData([]);
			setIsLoading(false);
		}
	};

	const changeSearchInput = (event: any) => {
		const searchVal = event.target.value;
		setSearchText(searchVal);
		updateTableQuery(defaultPage);
		getGroupsTableData(searchVal, defaultPage);
	};
	const debouncedSearchHandler = debounce(changeSearchInput, 500);

	const columns: readonly Column[] = [
		{
			id: 'groupName',
			label: 'Group Name',
			disablePadding: false,
			isSortable: true,
		},
		{
			id: 'dueDate',
			label: 'Due Date',
			disablePadding: false,
			isSortable: true,
		},
	];

	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag) {
			getGroupsTableData(searchText, data);
			updateTableQuery(data);
		}
	};
	const handleSortChange = (data: any) => {
		updateTableQuery(data);
		getGroupsTableData(searchText, data);
	};

	return (
		<Box
			className={styles['detailed-container']}
			role="presentation"
			data-testid="groups-container"
		>
			<div className={styles['groups-top-div']}>
				<div
					className={styles['groups-text-background']}
					data-testid="groups-top-div"
				>
					<Stack
						direction="row"
						justifyContent="flex-end"
						spacing={2}
						data-testid="close-button-stack"
					>
						<IconButton
							size="large"
							onClick={drawerCloseHandler}
							data-testid="close-groups-btn"
						>
							<DrawerClose fill={drawerCloseIconColor} />
						</IconButton>
					</Stack>

					<Box className={styles['groups-text-box']}>
						<Stack direction="row" justifyContent="space-between">
							<Typography
								className={styles['groups-text-name']}
								data-testid="riskGpt-text"
							>
								{toSentenceCase('Add to Group')}
							</Typography>
						</Stack>
					</Box>
				</div>
			</div>

			<Grid
				item
				className={styles['create-form-layout']}
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
			>
				<Grid
					item
					xs={12}
					md={12}
					lg={12}
					xl={12}
					className={styles['search-button']}
				>
					<StyledTextField
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchOutlined className={styles['input-search-icon']} />
								</InputAdornment>
							),
							inputProps: { 'data-testid': 'reginv-group-search' },
						}}
						className={styles['search-field']}
						id="fullWidth"
						placeholder="Search"
						data-testid="searchInput"
						onChange={debouncedSearchHandler}
					/>
				</Grid>

				<TableComponent
					defaultQuery={tableQueryObj}
					rows={groupsData}
					columns={columns}
					isLoading={isLoading}
					rowKey="groupId"
					onRowChecked={onRowCheckboxSelect}
					onPageHandler={handlePageChange}
					onSortHandler={handleSortChange}
					isCheckBoxEnabled
					defaultSortKey=""
					tableStyles={{ minWidth: '46.875em', maxHeight: '40vh' }}
					totalItems={groupsOriginalData?.totalCount || 0}
					enableHeaderColor
					isRenderPagination
				/>
			</Grid>
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
				p={1}
				className={styles['btn-position']}
			>
				<Stack direction="row" justifyContent="space-between">
					<Stack justifyContent="flex-start">
						<ButtonComp
							btnType="secondary"
							compClass={styles['create-button']}
							data-testid="create-btn"
							clickHandler={addNewGroup}
						>
							Create Group
						</ButtonComp>
					</Stack>

					<Stack
						direction="row"
						justifyContent="flex-end"
						className={styles['btn-groups-button']}
						spacing={2}
					>
						<ButtonComp
							btnType="secondary"
							compClass={styles['create-button']}
							data-testid="cancel-btn"
							clickHandler={onCancelHandler}
						>
							Cancel
						</ButtonComp>
						<ButtonComp
							btnType="primary"
							clickHandler={onAddGroupsHandler}
							data-testid="save-btn"
							disabled={selectedGroup.length === 0}
							compClass={styles['btn-save']}
						>
							Add
						</ButtonComp>
					</Stack>
				</Stack>
			</Grid>
		</Box>
	);
};

export default RegulatoryInventoryAddToGroup;
